import React, { Component } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { VictoryChart, VictoryLine, VictoryAxis } from 'victory';
import { getSites, getBlocsAffiches } from '../services/blocsAffichesService';
import PeriodSelector from './PeriodSelector';
import { rowsToCSV, downloadCSV } from '../lib/csv';

// Écran « Blocs affichés » (B10_02, D22/D27 B+F) — métrique INTERNE.
//
// Ce que ces compteurs mesurent : des FABRICATIONS DE BLOCS par le moteur (émission côté
// rendu serveur — ~99 % portées par notre propre egress). Ni de l'audience, ni du trafic :
// notre coût de rendu. Ils ont quitté tout écran client le 31/08 (bloc 5 → Consommation).
//
// Deux capteurs cohabitent ici et ne s'additionnent JAMAIS (POINT 31/08, partie 1) :
//  - les tuiles/courbes « blocs » = capteur APPLICATION (StatObject) ;
//  - la tuile « Préchargement » = capteur SERVEURS (StatLoad, logs nginx) — reprise de
//    l'écran client d'où elle a été retirée (D34) : c'est notre levier d'infrastructure
//    (~30 % de la charge des fronts).
//
// Victory est importé ICI pour la première fois du repo (arbitrage B10_02 D-loc-B) : la
// décision « barres, pas de graphiques » du 27/08 visait un écran SANS dimension
// temporelle ; celui-ci a une vraie série journalière. Primitives de base uniquement
// (VictoryChart/Line/Axis — stables entre versions).

// Groupes d'affichage (mapping D4, identique à views_by_type) : les tuiles ET les courbes
// somment `page_group + group_list` sous « Cercles ». La liste des objets BRUTS n'est PAS
// re-déclarée ici : la réponse porte `objects` (source de vérité serveur) — l'export CSV la
// consomme telle quelle, et tout objet servi mais absent de ce mapping est signalé à l'écran
// par `ungroupedObjects` (garde anti-dérive : précédent moderationAgents.js ↔ Agents.jsx,
// « aucun test ne rattrape la dérive » — ici un test la rattrape, cf. blocsAffiches.test.js).
export const DISPLAY_GROUPS = [
  { key: 'lists', label: 'Listes', objects: ['page_list'], color: '#1976d2' },
  { key: 'profiles', label: 'Profils', objects: ['page_profile'], color: '#9c27b0' },
  { key: 'groups', label: 'Cercles', objects: ['page_group', 'group_list'], color: '#2e7d32' },
  { key: 'search', label: 'Recherche', objects: ['search'], color: '#ed6c02' },
  { key: 'inbox', label: 'Messagerie', objects: ['inbox'], color: '#d32f2f' }
];

// Objets couverts par les groupes d'affichage (dérivé, jamais re-déclaré).
const GROUPED_OBJECTS = DISPLAY_GROUPS.reduce((acc, g) => acc.concat(g.objects), []);

const DAY_MS = 24 * 60 * 60 * 1000;
// Fenêtre initiale = celle du raccourci « 30 j » du PeriodSelector (to − 30 jours, 31 points),
// pour que le premier affichage et le clic sur le raccourci montrent la même chose.
const DEFAULT_WINDOW_DAYS = 30;

// YYYY-MM-DD : valeur d'un <input type="date"> ET borne envoyée à l'endpoint (jour calendaire,
// traité jour UTC côté API — sémantique B10_06/D35).
function toInputDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Somme des objets d'un groupe d'affichage sur un point (jour ou totaux).
export function groupValue(source, objects) {
  return objects.reduce((acc, object) => acc + (Number(source && source[object]) || 0), 0);
}

// Objets servis par l'API mais couverts par AUCUN groupe d'affichage. Non vide = un objet a
// été ajouté côté serveur (GRAPHDEBATE_APP_COUNTER_OBJECTS) sans mise à jour de ce mapping :
// l'écran l'affiche en avertissement au lieu de l'omettre en silence (le scénario exact du
// group_list presque oublié par le ticket, D-loc-A).
export function ungroupedObjects(objects) {
  return (objects || []).filter((o) => GROUPED_OBJECTS.indexOf(o) === -1);
}

// Lignes de l'export CSV : colonnes = `data.objects` (la taxonomie SERVEUR, objets bruts —
// l'écran groupe « Cercles », l'export non), une ligne par jour + ligne de totaux. Repli sur
// les objets groupés si `objects` manque (API antérieure). Pur et testé.
export function buildCsvRows(data) {
  const objects = (data && data.objects && data.objects.length) ? data.objects : GROUPED_OBJECTS;
  const rows = [['Date'].concat(objects)];
  ((data && data.daily) || []).forEach((d) => {
    rows.push([d.date].concat(objects.map((o) => String(Number(d[o]) || 0))));
  });
  rows.push([]);
  rows.push(['Total'].concat(objects.map((o) => String(Number(data && data.totals && data.totals[o]) || 0))));
  return rows;
}

class BlocsAffiches extends Component {
  constructor(props) {
    super(props);
    const to = new Date();
    const from = new Date(to.getTime() - DEFAULT_WINDOW_DAYS * DAY_MS);
    this.state = {
      sites: [],
      sitesLoading: true,
      websiteId: '',
      from: toInputDate(from),
      to: toInputDate(to),
      data: null,
      isLoading: false,
      error: null
    };
    this.fetchData = this.fetchData.bind(this);
    this.onRefresh = this.onRefresh.bind(this);
    this.handlePeriodChange = this.handlePeriodChange.bind(this);
    this.applyPeriod = this.applyPeriod.bind(this);
    this.handleSiteChange = this.handleSiteChange.bind(this);
    this.exportCSV = this.exportCSV.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    getSites()
      .then((result) => {
        if (!this._isMounted) return;
        const sites = (result && result.sites) || [];
        const websiteId = sites.length ? String(sites[0]._id) : '';
        this.setState({ sites, sitesLoading: false, websiteId }, () => {
          if (websiteId) this.fetchData();
        });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({
          sitesLoading: false,
          error: (err && err.message) || 'Erreur de chargement des sites'
        });
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Lecture seule : tout vient de l'endpoint, le front affiche, il n'agrège que les groupes.
  fetchData() {
    const { websiteId, from, to } = this.state;
    if (!websiteId) return;
    this.setState({ isLoading: true, error: null });
    getBlocsAffiches(websiteId, from, to)
      .then((result) => {
        if (!this._isMounted) return;
        // Garde anti-course : la réponse porte `website_id` — une réponse arrivée après un
        // changement de site (requête du site A résolue alors que le Select montre B) est
        // IGNORÉE. Sans elle, des chiffres seraient attribués au mauvais tenant (🟡1 review).
        if (result && result.website_id && String(result.website_id) !== this.state.websiteId) {
          return;
        }
        this.setState({ data: result, isLoading: false });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ error: (err && err.message) || 'Erreur de chargement', isLoading: false });
      });
  }

  onRefresh(e) {
    if (e && e.preventDefault) e.preventDefault();
    this.fetchData();
  }

  // Édition manuelle d'un champ date : on stocke, le rechargement attend « Rafraîchir ».
  handlePeriodChange(from, to) {
    this.setState({ from, to });
  }

  // Raccourci de période (7j/30j/trimestre) : on stocke ET on recharge immédiatement.
  applyPeriod(from, to) {
    this.setState({ from, to }, this.fetchData);
  }

  // Changement de site : `data: null` AVANT le fetch — les tuiles du site précédent ne
  // restent jamais affichées sous le nom du nouveau (🟡1 review). Le spinner initialLoading
  // reprend la main ; aucun enfant à état n'est démonté (l'écran est en lecture seule).
  handleSiteChange(e) {
    this.setState({ websiteId: e.target.value, data: null, error: null }, this.fetchData);
  }

  // Export CSV des objets BRUTS par jour (colonnes = taxonomie servie par l'API) + totaux.
  // Fidèle à la donnée servie, pas à l'affichage. Construction pure : buildCsvRows (testée).
  exportCSV() {
    const { data, from, to, sites, websiteId } = this.state;
    if (!data || !(data.daily || []).length) return;
    const site = sites.find((s) => String(s._id) === websiteId);
    const slug = site ? site.name.replace(/[^a-z0-9-]+/gi, '-') : websiteId;
    downloadCSV(`blocs-affiches_${slug}_${from}_${to}.csv`, rowsToCSV(buildCsvRows(data)));
  }

  renderTiles(totals) {
    const nf = new Intl.NumberFormat('fr-FR');
    return (
      <Grid container spacing={2}>
        {DISPLAY_GROUPS.map((g) => (
          <Grid item xs={6} md={2.4} key={g.key}>
            <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${g.color}` }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">{g.label}</Typography>
                <Typography variant="h5" data-testid={`blocs-total-${g.key}`}>
                  {nf.format(groupValue(totals, g.objects))}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  // Tuile « Préchargement » — capteur SERVEURS (StatLoad), visuellement séparée des blocs.
  // Trois états distincts, jamais confondus : mesuré / site non mesuré / lecteur en panne.
  renderPrefetchTile(data) {
    const nf = new Intl.NumberFormat('fr-FR');
    const prefetch = data.prefetch;
    const degraded = (data.degraded || []).indexOf('statLoadPeriodBreakdown') !== -1;

    let body;
    if (!prefetch) {
      body = (
        <Typography variant="body2" color="text.secondary">
          {degraded
            ? 'Lecteur de charge en panne sur cette requête — valeur indisponible (pas un zéro).'
            : 'Valeur indisponible.'}
        </Typography>
      );
    } else if (!prefetch.period_total) {
      body = (
        <Typography variant="body2" color="text.secondary">
          Site non mesuré par le compteur de charge (aucune requête dans StatLoad sur la période).
        </Typography>
      );
    } else {
      body = (
        <Stack direction="row" spacing={2} alignItems="baseline">
          <Typography variant="h5" data-testid="blocs-prefetch-total">
            {nf.format(prefetch.total)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            soit {(prefetch.share * 100).toFixed(1).replace('.', ',')} % de la charge brute
            servie ({nf.format(prefetch.period_total)} requêtes)
          </Typography>
        </Stack>
      );
    }

    return (
      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Préchargement</Typography>
            <Tooltip title="Capteur SERVEURS (StatLoad, logs nginx) — pas le capteur application des tuiles ci-dessus. Trafic déclenché par notre propre front pour anticiper les clics (~30 % de la charge des fronts) : levier d'infrastructure, retiré de l'écran client le 31/08.">
              <Chip size="small" variant="outlined" icon={<InfoOutlinedIcon />} label="capteur serveurs" />
            </Tooltip>
          </Stack>
          {body}
        </CardContent>
      </Card>
    );
  }

  renderChart(daily) {
    // Un point par jour et par groupe d'affichage. `daily` est REMPLIE par l'API (jours
    // vides à 0) : aucune interpolation par-dessus des trous.
    const series = DISPLAY_GROUPS.map((g) => ({
      ...g,
      points: daily.map((d) => ({ x: new Date(`${d.date}T00:00:00Z`), y: groupValue(d, g.objects) }))
    }));
    const tickFormat = (t) => {
      const d = t instanceof Date ? t : new Date(t);
      return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    };
    const nf = new Intl.NumberFormat('fr-FR');

    return (
      <Box data-testid="blocs-chart">
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {series.map((s) => (
            <Stack key={s.key} direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color }} />
              <Typography variant="caption">{s.label}</Typography>
            </Stack>
          ))}
        </Stack>
        <VictoryChart
          width={900}
          height={320}
          scale={{ x: 'time' }}
          padding={{ top: 16, bottom: 40, left: 70, right: 24 }}
        >
          <VictoryAxis
            tickFormat={tickFormat}
            fixLabelOverlap
            style={{ tickLabels: { fontSize: 11 } }}
          />
          <VictoryAxis
            dependentAxis
            tickFormat={(t) => nf.format(t)}
            style={{ tickLabels: { fontSize: 11 } }}
          />
          {series.map((s) => (
            <VictoryLine
              key={s.key}
              data={s.points}
              style={{ data: { stroke: s.color, strokeWidth: 1.5 } }}
            />
          ))}
        </VictoryChart>
      </Box>
    );
  }

  render() {
    const { sites, sitesLoading, websiteId, from, to, data, isLoading, error } = this.state;
    const daily = (data && data.daily) || [];
    // Spinner plein écran au premier chargement SEULEMENT : un rafraîchissement garde le
    // contenu monté (piège « démontage → état des enfants perdu », CLAUDE.md super-admin).
    const initialLoading = (sitesLoading || isLoading) && !data;

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Blocs affichés</Typography>
          <PeriodSelector
            from={from}
            to={to}
            isLoading={isLoading}
            onChange={this.handlePeriodChange}
            onApplyPeriod={this.applyPeriod}
            onRefresh={this.onRefresh}
            actions={
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={isLoading || !daily.length}
                onClick={this.exportCSV}
              >
                Exporter CSV
              </Button>
            }
          />
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Ces compteurs mesurent les <strong>fabrications de blocs par le moteur</strong> (capteur
          application, émis au rendu serveur — ~99 % portés par notre propre serveur). C'est notre
          coût de rendu, <strong>pas de l'audience</strong> : ils ne se comparent ni ne s'additionnent
          aux vues des écrans client. Recherche et Messagerie quasi nuls = nominal.
        </Alert>

        <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
          <InputLabel id="blocs-site-label">Site</InputLabel>
          <Select
            labelId="blocs-site-label"
            label="Site"
            value={websiteId}
            onChange={this.handleSiteChange}
            disabled={sitesLoading || isLoading}
            data-testid="blocs-site-select"
          >
            {sites.map((s) => (
              <MenuItem key={String(s._id)} value={String(s._id)}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {initialLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!initialLoading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Impossible de charger les blocs affichés : {error}
          </Alert>
        )}

        {!sitesLoading && !sites.length && !error && (
          <Typography variant="body2" color="text.secondary">
            Aucun tenant GraphDebate actif.
          </Typography>
        )}

        {!initialLoading && !error && data && (
          // data-website-id = attribut d'ÉTAT pour la recette : après un changement de site,
          // attendre cet attribut (jamais un texte — piège « assert text après wait » du
          // CLAUDE.md, précédent data-internal-version) prouve que le contenu affiché est
          // bien celui du tenant sélectionné.
          <Stack spacing={3} data-testid="blocs-content" data-website-id={String(data.website_id || '')}>
            {ungroupedObjects(data.objects).length > 0 && (
              <Alert severity="warning" data-testid="blocs-ungrouped">
                Objets servis par l'API mais absents des groupes de cet écran :{' '}
                <strong>{ungroupedObjects(data.objects).join(', ')}</strong>. Leurs volumes ne
                figurent ni dans les tuiles ni dans la courbe (l'export CSV, lui, les porte) —
                mettre à jour DISPLAY_GROUPS dans BlocsAffiches.js.
              </Alert>
            )}
            {this.renderTiles(data.totals || {})}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Série journalière{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  ({data.from} → {data.to})
                </Typography>
              </Typography>
              {daily.length ? (
                this.renderChart(daily)
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  Aucune donnée sur la période sélectionnée.
                </Typography>
              )}
            </Paper>

            {this.renderPrefetchTile(data)}
          </Stack>
        )}
      </Box>
    );
  }
}

export default BlocsAffiches;
